import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput } from 'flowbite-react';
import { v4 as uuidv4 } from 'uuid';

// Define the Zod schema for native team
const NativeTeamSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

// Define the form values type based on the schema
type NativeTeamFormValues = z.infer<typeof NativeTeamSchema>;

interface NativeTeamFormProps {
  onSubmit: (command: any) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  /** Called after the create command succeeds, with the new team's ID. */
  onCreated?: (id: string) => void;
}

const NativeTeamForm: React.FC<NativeTeamFormProps> = ({ onSubmit, onClose, isOpen, onCreated }) => {
  if (!isOpen) return null;

  const initialValues: NativeTeamFormValues = {
    name: '',
  };

  const handleSubmit = async (values: NativeTeamFormValues) => {
    // Generate a new ID for the team
    const teamId = uuidv4();
    
    // Create the command
    const command = {
      type: "CreateItem",
      id: teamId,
      item: {
        type: "Team",
        value: {
          type: "Native",
          name: values.name
        }
      }
    };

    await onSubmit(command);
    onCreated?.(teamId);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create New Team</h2>
        
        <Formik
          initialValues={initialValues}
          validationSchema={toFormikValidationSchema(NativeTeamSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Team Name</Label>
                </div>
                <Field
                  as={TextInput}
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="Enter team name"
                  required
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Create Team
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeTeamForm;
